import { assignInlineVars } from '@vanilla-extract/dynamic';
import clsx from 'clsx';
import type {
  CSSProperties,
  KeyboardEventHandler,
  MouseEventHandler,
  ReactNode,
} from 'react';
import { useMemo } from 'react';

import * as styles from './page-status-banner.css';
import type { PageStatusNotice } from './page-status-notice';

export type PageStatusBannerTone =
  | 'error'
  | 'processing'
  | 'success'
  | 'secondary'
  | 'warning';

export interface PageStatusBannerProps {
  ariaLive?: 'assertive' | 'off' | 'polite';
  ariaExpanded?: boolean;
  ariaLabel?: string;
  children?: ReactNode;
  collapsed?: boolean;
  collapsible?: boolean;
  icon?: ReactNode;
  notice?: PageStatusNotice;
  onClick?: MouseEventHandler<HTMLDivElement>;
  onKeyDown?: KeyboardEventHandler<HTMLDivElement>;
  placement?: 'overlay' | 'stack';
  role?: string;
  size?: 'normal' | 'compact';
  statusId?: string;
  style?: CSSProperties;
  tabIndex?: number;
  title?: string;
  tone?: PageStatusBannerTone;
}

const toneStyles: Record<
  PageStatusBannerTone,
  { backgroundColor: string; borderColor: string; color: string }
> = {
  error: {
    backgroundColor:
      'color-mix(in srgb, var(--streamlain-error-color) 14%, transparent)',
    borderColor:
      'color-mix(in srgb, var(--streamlain-error-color) 34%, transparent)',
    color: 'var(--streamlain-error-color)',
  },
  processing: {
    backgroundColor:
      'color-mix(in srgb, var(--streamlain-processing-color) 14%, transparent)',
    borderColor:
      'color-mix(in srgb, var(--streamlain-processing-color) 34%, transparent)',
    color: 'var(--streamlain-processing-color)',
  },
  success: {
    backgroundColor:
      'color-mix(in srgb, var(--streamlain-success-color) 14%, transparent)',
    borderColor:
      'color-mix(in srgb, var(--streamlain-success-color) 34%, transparent)',
    color: 'var(--streamlain-success-color)',
  },
  secondary: {
    backgroundColor:
      'color-mix(in srgb, var(--streamlain-text-secondary-color) 14%, transparent)',
    borderColor:
      'color-mix(in srgb, var(--streamlain-text-secondary-color) 22%, transparent)',
    color: 'var(--streamlain-text-secondary-color)',
  },
  warning: {
    backgroundColor:
      'color-mix(in srgb, var(--streamlain-warning-color) 14%, transparent)',
    borderColor:
      'color-mix(in srgb, var(--streamlain-warning-color) 34%, transparent)',
    color: 'var(--streamlain-warning-color)',
  },
};

export function PageStatusBanner({
  ariaLive,
  ariaExpanded,
  ariaLabel,
  children,
  collapsed,
  icon,
  notice,
  onClick,
  onKeyDown,
  placement = 'overlay',
  role,
  size,
  statusId,
  style,
  tabIndex,
  title,
  tone,
}: PageStatusBannerProps) {
  const resolvedTone = notice?.tone ?? tone;
  const resolvedChildren = notice?.children ?? children;
  const resolvedIcon = notice?.icon ?? icon;
  const resolvedSize = notice?.size ?? size ?? 'normal';
  const resolvedRole = notice?.role ?? role ?? 'status';
  const resolvedAriaLive = notice?.ariaLive ?? ariaLive ?? 'polite';
  const resolvedTitle = notice?.title ?? title;
  const resolvedStatusId =
    notice?.id ?? statusId ?? `${resolvedTone ?? 'status'}:${resolvedSize}`;
  const resolvedCollapsed = collapsed === true;

  const toneStyle = resolvedTone ? toneStyles[resolvedTone] : null;
  const styleVars = useMemo(
    () =>
      toneStyle
        ? assignInlineVars({
            [styles.backgroundColorVar]: toneStyle.backgroundColor,
            [styles.borderColorVar]: toneStyle.borderColor,
            [styles.textColorVar]: toneStyle.color,
            [styles.heightVar]: resolvedSize === 'compact' ? '32px' : '40px',
          })
        : undefined,
    [resolvedSize, toneStyle]
  );

  if (!resolvedTone || !resolvedChildren) {
    return null;
  }

  return (
    <div
      role={resolvedRole}
      aria-live={resolvedAriaLive}
      aria-expanded={ariaExpanded}
      aria-label={ariaLabel}
      className={clsx(
        styles.root,
        placement === 'stack' && styles.stackItem,
        resolvedSize === 'compact' ? styles.compact : styles.normal,
        resolvedCollapsed && styles.collapsedRoot,
        placement === 'stack' && resolvedCollapsed && styles.stackCollapsed,
        placement === 'stack' && !resolvedCollapsed && styles.stackExpanded
      )}
      onClick={onClick}
      onKeyDown={onKeyDown}
      tabIndex={tabIndex}
      data-status-banner="true"
      data-status-id={resolvedStatusId}
      data-placement={placement}
      data-tone={resolvedTone}
      data-size={resolvedSize}
      data-collapsed={resolvedCollapsed ? 'true' : 'false'}
      title={resolvedTitle}
      style={{
        ...styleVars,
        ...style,
      }}
    >
      <span
        className={clsx(
          styles.content,
          resolvedCollapsed && styles.collapsedContent
        )}
      >
        {resolvedIcon ? (
          <span
            className={clsx(
              styles.icon,
              resolvedCollapsed && styles.collapsedIcon
            )}
            aria-hidden="true"
          >
            {resolvedIcon}
          </span>
        ) : null}
        <span
          className={clsx(
            styles.text,
            resolvedCollapsed && styles.collapsedText
          )}
        >
          {resolvedChildren}
        </span>
      </span>
    </div>
  );
}
